// =============================================================
// publicApi.js — UNAUTHENTICATED client for DAM external share links.
//
// Part of the removable DAM module. Deliberately does NOT import the MSAL-backed
// `api` client (frontend/src/api/client.js): these calls are made by external
// recipients with no Harvus login, gated only by the unguessable token in the
// URL. Plain `fetch`, no Authorization header.
// =============================================================

import { resumableUpload } from '../blockUpload';

const BASE = '/api';

async function req(path, { method = 'GET', body } = {}) {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try { data = await res.json(); } catch { /* non-JSON / empty */ }
  if (!res.ok) {
    const err = new Error((data && (data.error || data.reason)) || `Request failed (${res.status})`);
    err.status = res.status;
    err.reason = data && data.reason;
    throw err;
  }
  return data;
}

// Resolve link metadata (kind + asset info or upload spec). Logs a 'view'.
export function resolveShare(token) {
  return req(`/dam/share/${encodeURIComponent(token)}`);
}

// Absolute-ish download URL the browser can hit directly (server sets an
// attachment Content-Disposition, so navigating/clicking triggers a download).
export function buildDownloadUrl(token, params = {}) {
  const clean = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== '') clean[k] = v;
  });
  const qs = new URLSearchParams(clean).toString();
  return `${BASE}/dam/share/${encodeURIComponent(token)}/download${qs ? `?${qs}` : ''}`;
}

// Longest-edge cap for the AI-tagging image. Claude's vision API resizes large
// images down anyway, and its ~5MB base64 limit means large originals (e.g. a
// full-res phone photo) can't be sent as-is — so we downscale in the browser.
// Mirrors damApi.js (the authenticated upload path); duplicated rather than
// imported so the public bundle needn't pull in the MSAL-backed api client.
const TAG_IMAGE_MAX_EDGE = 1568;
const THUMB_MAX_EDGE = 512;

// Draw `img` into a downscaled JPEG (longest edge capped at `maxEdge`) and return
// its base64 payload, or '' on any canvas error (tainted/unsupported source).
function downscaleToJpegBase64(img, maxEdge, quality) {
  try {
    const longest = Math.max(img.naturalWidth, img.naturalHeight) || 1;
    const scale = Math.min(1, maxEdge / longest);
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d').drawImage(img, 0, 0, w, h);
    return (canvas.toDataURL('image/jpeg', quality).split(',')[1]) || '';
  } catch {
    return '';
  }
}

// Load an image File once and (best-effort) return its pixel dimensions, a
// downscaled JPEG for server-side AI tagging, and a smaller thumbnail JPEG for
// the grid. Resolves { dims:{}, tag:null, thumb:null } for non-images or on
// error. The tag image lets the API auto-tag originals over Claude's size cap,
// or in formats (HEIC, TIFF…) the browser can render but the vision API can't
// accept directly — without it, large/phone photos skip tagging entirely.
function loadImageForUpload(file) {
  return new Promise((resolve) => {
    if (!file.type?.startsWith('image/')) return resolve({ dims: {}, tag: null, thumb: null });
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const dims = { width: img.naturalWidth, height: img.naturalHeight };
      const tagBase64 = downscaleToJpegBase64(img, TAG_IMAGE_MAX_EDGE, 0.85);
      const thumbBase64 = downscaleToJpegBase64(img, THUMB_MAX_EDGE, 0.8);
      URL.revokeObjectURL(url);
      resolve({
        dims,
        tag: tagBase64 ? { base64: tagBase64, mimeType: 'image/jpeg' } : null,
        thumb: thumbBase64 ? { base64: thumbBase64, mimeType: 'image/jpeg' } : null,
      });
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve({ dims: {}, tag: null, thumb: null }); };
    img.src = url;
  });
}

// Full external-upload flow: request a scoped SAS write URL → stage the file to
// Blob Storage in resumable blocks (survives network drops / reloads, reports
// byte progress) → confirm (server marks ready, AI-tags, logs an 'upload'). The
// confirm sends a downscaled tag image + thumbnail so shared uploads get the
// same AI tagging as internal uploads. `onProgress({ loaded, total })` fires
// during the block staging.
export async function uploadViaShare(token, file, { onProgress } = {}) {
  const { dims, tag, thumb } = await loadImageForUpload(file);

  const { assetId } = await resumableUpload(file, {
    getUploadUrl: (extra) => req(`/dam/share/${encodeURIComponent(token)}/upload-url`, {
      method: 'POST',
      body: {
        fileName: file.name,
        fileType: (file.name.split('.').pop() || '').toLowerCase(),
        mimeType: file.type || null,
        fileSizeBytes: file.size,
        // The folder this file came in with, when the uploader added a whole
        // directory rather than picking files. The server normalizes it (this
        // endpoint is public) and uses it three ways: it is recorded, it reads
        // raw vs. edited off folder names like "Raw" / "Edited", and it groups
        // the batch in the triage queue on our side.
        relativePath: file.webkitRelativePath || file.harvusRelativePath || null,
        ...extra,
      },
    }),
    onProgress,
  });

  await req(`/dam/share/${encodeURIComponent(token)}/upload-confirm`, {
    method: 'POST',
    body: {
      assetId,
      width: dims.width ?? null,
      height: dims.height ?? null,
      fileSizeBytes: file.size,
      mimeType: file.type || null,
      // Downscaled JPEG for server-side AI tagging (best-effort; null for non-images).
      tagImageBase64: tag?.base64 || null,
      tagImageMimeType: tag?.mimeType || null,
      // Small thumbnail JPEG stored for the internal grid (best-effort).
      thumbBase64: thumb?.base64 || null,
      thumbMimeType: thumb?.mimeType || null,
    },
  });
  return { assetId };
}

// Local byte formatter (kept here so the public bundle needn't import damApi,
// which would drag in the MSAL client).
export function formatBytes(n) {
  if (n == null || Number.isNaN(Number(n))) return '';
  n = Number(n);
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024, i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}
